/**
 * Vista "Mermas" (RES-47): top mermas por motivo (barras), tabla por insumo y comparación por
 * local con el costo de cada motivo.
 */
import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  formatCompactCurrency,
  formatQty,
  money,
  wasteReasonsPresent,
} from '../../utils/inventoryReports';
import { ChartTooltip, KpiTiles, MoneyNote, SortableTable, axisProps } from './ReportParts';

const ROW_HEIGHT = 32;

function WasteReport({ data, t, locale, colors, nameOf, reasonLabel }) {
  const totals = money(data?.totals);
  const movements = Number(data?.totals?.movements) || 0;
  const reasons = useMemo(() => wasteReasonsPresent(data), [data]);
  const byReason = useMemo(
    () =>
      (data?.byReason || []).map((r) => ({
        reasonCode: r.reasonCode,
        name: reasonLabel(r.reasonCode),
        value: money(r.cost).value,
        movements: r.movements,
      })),
    [data, reasonLabel]
  );
  const byRestaurant = data?.byRestaurant || [];
  const showCompare = Boolean(data?.consolidated) || byRestaurant.length > 1;
  const topReason = byReason[0];

  const topReasonsOf = (item) =>
    Object.entries(item.reasons || {})
      .sort(([, a], [, b]) => b - a)
      .map(([code, qty]) => `${reasonLabel(code)} (${formatQty(qty, locale)})`)
      .join(', ');

  return (
    <div className="report-view" data-view="waste">
      <KpiTiles
        tiles={[
          {
            key: 'cost',
            label: t('inventory.reports.waste.kpiCost'),
            value: formatCurrency(totals.value),
            sub: <MoneyNote amount={data?.totals} t={t} />,
          },
          {
            key: 'movements',
            label: t('inventory.reports.waste.kpiMovements'),
            value: String(movements),
          },
          {
            key: 'topReason',
            label: t('inventory.reports.waste.kpiTopReason'),
            value: topReason ? topReason.name : '—',
            sub: topReason ? formatCurrency(topReason.value) : undefined,
          },
        ]}
      />

      <section className="report-card">
        <h3>{t('inventory.reports.waste.chartTitle')}</h3>
        {byReason.length > 0 ? (
          <div
            className="report-chart"
            role="img"
            aria-label={t('inventory.reports.waste.chartTitle')}
          >
            <ResponsiveContainer width="100%" height={byReason.length * ROW_HEIGHT + 40}>
              <BarChart
                data={byReason}
                layout="vertical"
                margin={{ top: 4, right: 24, left: 8, bottom: 4 }}
              >
                <CartesianGrid horizontal={false} stroke={colors.grid} />
                <XAxis
                  type="number"
                  {...axisProps(colors)}
                  tickFormatter={(v) => formatCompactCurrency(v, locale)}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={140}
                  {...axisProps(colors)}
                  tick={{ fill: colors.text, fontSize: 12 }}
                />
                <Tooltip
                  cursor={{ fill: colors.grid, opacity: 0.4 }}
                  content={
                    <ChartTooltip
                      rows={(payload) => {
                        const row = payload[0].payload;
                        return [
                          {
                            key: 'cost',
                            label: t('inventory.reports.columns.cost'),
                            value: formatCurrency(row.value),
                            color: colors.series1,
                          },
                          {
                            key: 'movements',
                            label: t('inventory.reports.columns.movements'),
                            value: String(row.movements),
                          },
                        ];
                      }}
                    />
                  }
                />
                <Bar
                  dataKey="value"
                  fill={colors.series1}
                  radius={[0, 4, 4, 0]}
                  barSize={16}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="report-hint">{t('inventory.reports.states.noChartData')}</p>
        )}
      </section>

      {showCompare && (
        <section className="report-card">
          <h3>{t('inventory.reports.compareTitle')}</h3>
          <SortableTable
            t={t}
            testId="waste-by-restaurant"
            rows={byRestaurant}
            rowKey={(r) => r.restaurantId}
            defaultSort={{ key: 'cost', direction: 'desc' }}
            columns={[
              {
                key: 'name',
                label: t('inventory.reports.columns.restaurant'),
                sortValue: (r) => nameOf(r.restaurantId),
                render: (r) => nameOf(r.restaurantId),
              },
              {
                key: 'movements',
                label: t('inventory.reports.columns.movements'),
                align: 'right',
                sortValue: (r) => r.movements,
                render: (r) => r.movements,
              },
              {
                key: 'cost',
                label: t('inventory.reports.columns.cost'),
                align: 'right',
                sortValue: (r) => money(r.cost).value,
                render: (r) => (
                  <>
                    {formatCurrency(money(r.cost).value)}
                    <MoneyNote amount={r.cost} t={t} />
                  </>
                ),
              },
              ...reasons.map((code) => ({
                key: `reason-${code}`,
                label: reasonLabel(code),
                align: 'right',
                sortValue: (r) => money(r.byReason?.[code]).value,
                render: (r) =>
                  r.byReason?.[code] ? formatCurrency(money(r.byReason[code]).value) : '—',
              })),
            ]}
          />
        </section>
      )}

      <section className="report-card">
        <h3>{t('inventory.reports.waste.tableTitle')}</h3>
        <SortableTable
          t={t}
          testId="waste-table"
          rows={data?.byItem || []}
          rowKey={(r) => r.itemId}
          defaultSort={{ key: 'cost', direction: 'desc' }}
          columns={[
            {
              key: 'name',
              label: t('inventory.reports.columns.item'),
              sortValue: (r) => r.name,
              render: (r) => r.name,
            },
            {
              key: 'quantity',
              label: t('inventory.reports.columns.quantity'),
              align: 'right',
              sortValue: (r) => r.quantity,
              render: (r) => `${formatQty(r.quantity, locale)} ${r.unit}`,
            },
            {
              key: 'movements',
              label: t('inventory.reports.columns.movements'),
              align: 'right',
              sortValue: (r) => r.movements,
              render: (r) => r.movements,
            },
            {
              key: 'cost',
              label: t('inventory.reports.columns.cost'),
              align: 'right',
              sortValue: (r) => money(r.cost).value,
              render: (r) => (
                <>
                  {formatCurrency(money(r.cost).value)}
                  <MoneyNote amount={r.cost} t={t} />
                </>
              ),
            },
            {
              key: 'reasons',
              label: t('inventory.reports.columns.reasons'),
              render: topReasonsOf,
            },
          ]}
        />
      </section>
    </div>
  );
}

export default WasteReport;
